import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaEye, FaPaperclip, FaSave, FaTrash, FaUserPlus, FaPhone, FaUser, FaMapMarkerAlt } from 'react-icons/fa';
import {
  createReportIncidentAssignment,
  deleteReportIncidentAssignment,
  getReportIncidentDepartments,
  getReportIncidentAssignments,
  getReportIncidents,
  updateReportIncidentStatus
} from '../../services/reportIncidentService';
import { BASE_URL } from '../../services/api';
import './ReportIncidents.scss';

const statusOptions = [
  { value: '', label: 'Tümü' },
  { value: 'New', label: 'Yeni' },
  { value: 'InProgress', label: 'İşlemde' },
  { value: 'Resolved', label: 'Çözüldü' },
  { value: 'Rejected', label: 'Reddedildi' }
];

const statusLabels = {
  New: 'Yeni',
  InProgress: 'İşlemde',
  Resolved: 'Çözüldü',
  Rejected: 'Reddedildi'
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('tr-TR');
};

const resolveFileUrl = (path) => {
  if (!path) return '#';
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path}`;
};

// Parse "Konum: 36.585, 36.187" from eventLocation string
const parseCoords = (text) => {
  if (!text) return null;
  const match = text.match(/Konum:\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!match) return null;
  const lat = parseFloat(match[1]);
  const lon = parseFloat(match[2]);
  if (isNaN(lat) || isNaN(lon)) return null;
  return { lat, lon };
};

// Parse "[Sistem Bilgisi: En yakın süpüren personel: NAME (Kullanıcı Adı: USERNAME, Tel: PHONE, Mesafe: Xm)]"
const parsePersonnelInfo = (text) => {
  if (!text) return null;
  const match = text.match(/\[Sistem Bilgisi: En yakın [^:]+: (.+?) \(Kullanıcı Adı: (.+?)(?:, Tel: (.+?))?, Mesafe: (.+?)m?\)\]/);
  if (!match) return null;
  const fullName = match[1]?.trim() || '';
  const username = match[2]?.trim() || '';
  const phone = match[3]?.trim() || '';
  const distance = match[4]?.trim() || '';
  const nameParts = fullName ? fullName.split(' ') : [];
  const firstName = nameParts.slice(0, -1).join(' ') || username;
  const lastName = nameParts.slice(-1)[0] || '';
  return { fullName, firstName, lastName, username, phone, distance };
};

// Strip [Sistem Bilgisi: ...] block from description text
const stripSystemInfo = (text) => {
  if (!text) return '';
  return text.replace(/\n*\[Sistem Bilgisi:.*?\]/gs, '').trim();
};

// Strip ", Harita: URL" suffix from eventLocation — we show an embedded map instead
const stripLocationMapLink = (text) => {
  if (!text) return '';
  return text
    .replace(/,?\s*Harita:\s*https?:\/\/[^\s,]*/gi, '')
    .trim()
    .replace(/,\s*$/, '');
};

// Parse "Durum: ..." from eventLocation → e.g. "Son 2 Saat İçinde Süpürüldü"
const parseLocationStatus = (text) => {
  if (!text) return null;
  const match = text.match(/Durum:\s*([^,\[]+)/i);
  return match ? match[1].trim() : null;
};

// Parse "No: ..." from eventLocation for containers
const parseContainerNo = (text) => {
  if (!text) return null;
  const match = text.match(/\[Konteyner Şikayeti\]\s*No:\s*([^,\[]+)/i);
  return match ? match[1].trim() : null;
};

// Parse "Temizlik Zamanı: ..." from eventLocation
const parseCleanTime = (text) => {
  if (!text) return null;
  const match = text.match(/Temizlik Zamanı:\s*([^,\[]+)/i);
  return match ? match[1].trim() : null;
};

// Generate Leaflet popup text for the pin
const getMapPopupText = (text) => {
  if (!text) return '';
  if (text.includes('[Konteyner Şikayeti]')) {
    const binNo = parseContainerNo(text);
    const adresMatch = text.match(/Adres:\s*([^,\[]+)/i);
    const adres = adresMatch ? adresMatch[1].trim() : '';
    return `<div style="font-family:inherit;font-size:13px;line-height:1.4;color:#1e293b;margin: 5px 0;">
      <strong style="color:#2563eb;font-size:14px;display:block;margin-bottom:4px;">Konteyner Bilgisi</strong>
      <strong>No:</strong> ${binNo || '-'}<br/>
      ${adres ? `<strong>Adres:</strong> ${adres}` : ''}
    </div>`;
  }
  return `<div style="font-family:inherit;font-size:13px;color:#1e293b;padding:4px 0;"><strong>Olay Konumu</strong></div>`;
};


const renderTextWithLinks = (text) => {
  if (!text) return '-';
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      let url = part;
      let suffix = '';
      if (/[.,]$/.test(url)) {
        suffix = url.slice(-1);
        url = url.slice(0, -1);
      }
      const isGoogleMaps = url.includes('google.com/maps');
      return (
        <React.Fragment key={index}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#2563eb',
              textDecoration: 'underline',
              fontWeight: 600,
              wordBreak: 'break-all'
            }}
          >
            {isGoogleMaps ? ' Haritada Gör' : url}
          </a>
          {suffix}
        </React.Fragment>
      );
    }
    return <span key={index}>{part}</span>;
  });
};

// Load Leaflet via CDN (same pattern as GarbageServices — no npm leaflet in this workspace)
const ensureLeafletForMap = () => new Promise((resolve, reject) => {
  if (window.L) { resolve(window.L); return; }

  const cssId = 'leaflet-css-ri';
  if (!document.getElementById(cssId)) {
    const link = document.createElement('link');
    link.id = cssId;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.crossOrigin = '';
    document.head.appendChild(link);
  }

  const scriptId = 'leaflet-js-ri';
  const existing = document.getElementById(scriptId);
  if (existing) {
    if (window.L) { resolve(window.L); return; }
    existing.addEventListener('load', () => resolve(window.L));
    existing.addEventListener('error', reject);
    return;
  }

  const script = document.createElement('script');
  script.id = scriptId;
  script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  script.async = true;
  script.onload = () => resolve(window.L);
  script.onerror = reject;
  document.body.appendChild(script);
});

// ── Embedded map component with a pin marker ──────────────────────────────
const LocationMap = ({ lat, lon, popupText }) => {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || leafletRef.current) return;

    let destroyed = false;

    // ── Scroll fix: forward wheel events to the nearest scrollable parent ──
    // Leaflet captures wheel even when scrollWheelZoom:false, blocking modal scroll.
    const onWheel = (e) => {
      let el = mapRef.current?.parentElement;
      while (el) {
        const style = window.getComputedStyle(el);
        const ov = style.overflow + style.overflowY;
        if (/auto|scroll/.test(ov) && el.scrollHeight > el.clientHeight) {
          el.scrollTop += e.deltaY;
          return;
        }
        el = el.parentElement;
      }
    };
    mapRef.current.addEventListener('wheel', onWheel, { passive: true });

    ensureLeafletForMap().then((L) => {
      if (destroyed || !mapRef.current || leafletRef.current) return;

      const map = L.map(mapRef.current, {
        center: [lat, lon],
        zoom: 17,
        zoomControl: true,
        scrollWheelZoom: false,
        dragging: true,
        attributionControl: false
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19
      }).addTo(map);

      // Custom red pin icon
      const pinIcon = L.divIcon({
        className: '',
        html: `<div style="width:28px;height:36px;">
          <svg viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22s14-12.667 14-22C28 6.268 21.732 0 14 0z" fill="#dc2626"/>
            <circle cx="14" cy="14" r="6" fill="#fff"/>
          </svg>
        </div>`,
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        popupAnchor: [0, -36]
      });

      const marker = L.marker([lat, lon], { icon: pinIcon }).addTo(map);
      if (popupText) {
        marker.bindPopup(popupText).openPopup();
      }
      leafletRef.current = map;
    }).catch(console.error);

    const containerEl = mapRef.current;
    return () => {
      destroyed = true;
      containerEl?.removeEventListener('wheel', onWheel);
      if (leafletRef.current) {
        leafletRef.current.remove();
        leafletRef.current = null;
      }
    };
  }, [lat, lon, popupText]);

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: '100%', borderRadius: 0, overflow: 'hidden', zIndex: 0 }}
    />
  );
};



const ReportIncidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [filters, setFilters] = useState({ status: '', departmentGuid: '' });
  const [loading, setLoading] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: 'InProgress', staffNote: '' });
  const [assignmentForm, setAssignmentForm] = useState({ userEmail: '', departmentGuid: '' });

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);
  const role = (user.userType || user.role || '').toLowerCase();
  const isAdmin = role === 'admin' || role === '99';

  useEffect(() => {
    loadDepartments();
    loadIncidents();
    if (isAdmin) loadAssignments();
  }, []);

  const loadDepartments = async () => {
    try {
      const response = await getReportIncidentDepartments();
      setDepartments(response?.isSuccess && Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Departments could not be loaded', error);
    }
  };

  const loadIncidents = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const response = await getReportIncidents(nextFilters);
      if (response?.isSuccess) {
        setIncidents(response.data || []);
      } else {
        toast.error(response?.resultMessage || 'Bildirimler yüklenemedi.');
      }
    } catch (error) {
      toast.error('Bildirimler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      const response = await getReportIncidentAssignments();
      if (response?.isSuccess) {
        setAssignments(response.data || []);
      }
    } catch (error) {
      console.error('Assignments could not be loaded', error);
    }
  };

  const openDetails = (incident) => {
    setSelectedIncident(incident);
    setStatusForm({
      status: incident.status || 'InProgress',
      staffNote: incident.staffNote || ''
    });
  };

  const handleFilterChange = (field, value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);
    loadIncidents(next);
  };

  const saveStatus = async () => {
    if (!selectedIncident) return;

    try {
      const response = await updateReportIncidentStatus(selectedIncident.id, statusForm);
      if (response?.isSuccess) {
        toast.success('Bildirim durumu güncellendi.');
        setSelectedIncident(null);
        loadIncidents();
      } else {
        toast.error(response?.resultMessage || 'Durum güncellenemedi.');
      }
    } catch (error) {
      toast.error('Durum güncellenemedi.');
    }
  };

  const saveAssignment = async () => {
    const department = departments.find((item) => item.guid === assignmentForm.departmentGuid);
    if (!assignmentForm.userEmail || !department) {
      toast.error('E-posta ve birim seçiniz.');
      return;
    }

    try {
      const response = await createReportIncidentAssignment({
        userEmail: assignmentForm.userEmail,
        departmentGuid: assignmentForm.departmentGuid,
        departmentName: department.name
      });
      if (response?.isSuccess) {
        toast.success('Yetki ataması kaydedildi.');
        setAssignmentForm({ userEmail: '', departmentGuid: '' });
        loadAssignments();
      }
    } catch (error) {
      toast.error('Yetki ataması kaydedilemedi.');
    }
  };

  const removeAssignment = async (id) => {
    try {
      const response = await deleteReportIncidentAssignment(id);
      if (response?.isSuccess) {
        toast.success('Yetki ataması pasife alındı.');
        loadAssignments();
      }
    } catch (error) {
      toast.error('Yetki ataması silinemedi.');
    }
  };

  const isImageAttachment = (attachment) =>
    attachment?.mediaType === 'Photo' || attachment?.contentType?.startsWith('image/');

  const isVideoAttachment = (attachment) =>
    attachment?.mediaType === 'Video' || attachment?.contentType?.startsWith('video/');

  // Derived data for the detail modal
  const detailCoords = selectedIncident ? parseCoords(selectedIncident.eventLocation) : null;
  const personnelInfo = selectedIncident ? parsePersonnelInfo(selectedIncident.eventDescription) : null;
  const cleanDescription = selectedIncident ? stripSystemInfo(selectedIncident.eventDescription) : '';

  return (
    <div className="report-incidents-page">
      <div className="page-header">
        <div>
          <h1>Bize Bildir Yönetimi</h1>
          <p>Vatandaş bildirimlerini ilgili birime göre takip edin.</p>
        </div>
      </div>

      {isAdmin && (
        <section className="assignment-panel">
          <h2>Birim Personeli Yetkilendirme</h2>
          <div className="assignment-form">
            <input
              type="email"
              placeholder="personel@iskenderun.bel.tr"
              value={assignmentForm.userEmail}
              onChange={(e) => setAssignmentForm((prev) => ({ ...prev, userEmail: e.target.value }))}
            />
            <select
              value={assignmentForm.departmentGuid}
              onChange={(e) => setAssignmentForm((prev) => ({ ...prev, departmentGuid: e.target.value }))}
            >
              <option value="">Birim seçiniz</option>
              {departments.map((department) => (
                <option key={department.guid} value={department.guid}>
                  {department.name}
                </option>
              ))}
            </select>
            <button type="button" onClick={saveAssignment}>
              <FaUserPlus /> Yetki Ver
            </button>
          </div>
          <div className="assignment-list">
            {assignments.map((assignment) => (
              <div key={assignment.id} className={assignment.isActive ? '' : 'is-passive'}>
                <span>{assignment.userEmail}</span>
                <strong>{assignment.departmentName}</strong>
                <button type="button" onClick={() => removeAssignment(assignment.id)} disabled={!assignment.isActive}>
                  <FaTrash />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="incidents-table">
        <table>
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Vatandaş</th>
              <th>Telefon</th>
              <th>Birim</th>
              <th>Konum</th>
              <th>Durum</th>
              <th>Ek</th>
              <th />
            </tr>
            <tr className={`table-filter-row ${isAdmin ? 'has-department-filter' : 'status-only'}`}>
              <th />
              <th />
              <th />
              <th>
                {isAdmin && (
                  <select value={filters.departmentGuid} onChange={(e) => handleFilterChange('departmentGuid', e.target.value)}>
                    <option value="">Tüm birimler</option>
                    {departments.map((department) => (
                      <option key={department.guid} value={department.guid}>
                        {department.name}
                      </option>
                    ))}
                  </select>
                )}
              </th>
              <th />
              <th>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}>Yükleniyor...</td></tr>
            ) : incidents.length === 0 ? (
              <tr><td colSpan={8}>Bildirim bulunamadı.</td></tr>
            ) : incidents.map((incident) => (
              <tr key={incident.id}>
                <td>{formatDate(incident.createdDate)}</td>
                <td>{incident.firstName} {incident.lastName}</td>
                <td>{incident.phone}</td>
                <td>{incident.departmentName}</td>
                <td>{stripLocationMapLink(incident.eventLocation)}</td>
                <td><span className={`status-pill ${incident.status}`}>{statusLabels[incident.status] || incident.status}</span></td>
                <td>{incident.attachments?.length > 0 ? <><FaPaperclip /> {incident.attachments.length}</> : '-'}</td>
                <td>
                  <button type="button" className="icon-button" onClick={() => openDetails(incident)}>
                    <FaEye />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={!!selectedIncident} onHide={() => setSelectedIncident(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bildirim Detayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedIncident && (
            <div className="incident-detail">
              {/* ── Top info grid ── */}
              <div className="detail-grid">
                <div><span>Vatandaş</span><strong>{selectedIncident.firstName} {selectedIncident.lastName}</strong></div>
                <div><span>Telefon</span><strong>{selectedIncident.phone}</strong></div>
                <div><span>Birim</span><strong>{selectedIncident.departmentName}</strong></div>
                <div><span>Tarih</span><strong>{formatDate(selectedIncident.createdDate)}</strong></div>
              </div>



              {/* ── Map + Personnel side-by-side squares ── */}
              {(detailCoords || personnelInfo) && (
                <div className="map-personnel-grid">
                  {detailCoords && (
                    <div className="map-personnel-grid__cell">
                      <div className="map-personnel-grid__label">Konum</div>
                      <div className="map-personnel-grid__map">
                        <LocationMap lat={detailCoords.lat} lon={detailCoords.lon} popupText={getMapPopupText(selectedIncident.eventLocation)} />
                      </div>
                    </div>
                  )}
                  {personnelInfo && (
                    <div className="map-personnel-grid__cell personnel-card">
                      <div className="personnel-card__header">
                        <FaUser />
                        <span>Konumu Temizleyen Personel</span>
                        {personnelInfo.distance && (
                          <em className="personnel-card__distance">
                            <FaMapMarkerAlt /> {personnelInfo.distance}m
                          </em>
                        )}
                      </div>
                      <div className="personnel-card__body">
                        {/* Ad Soyad */}
                        {(personnelInfo.fullName && personnelInfo.fullName !== personnelInfo.username) ? (
                          <div className="personnel-card__field">
                            <span>Ad Soyad</span>
                            <strong>{personnelInfo.fullName}</strong>
                          </div>
                        ) : (
                          <div className="personnel-card__field">
                            <span>Kullanıcı</span>
                            <strong>{personnelInfo.username}</strong>
                          </div>
                        )}
                        {personnelInfo.phone && personnelInfo.phone !== personnelInfo.username && (
                          <div className="personnel-card__field">
                            <span>Telefon</span>
                            <strong>
                              <a href={`tel:${personnelInfo.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                                <FaPhone style={{ marginRight: 5, fontSize: '0.8em' }} />
                                {personnelInfo.phone}
                              </a>
                            </strong>
                          </div>
                        )}
                        {/* Konteyner Numarası */}
                        {parseContainerNo(selectedIncident?.eventLocation) && (
                          <div className="personnel-card__field">
                            <span>Konteyner No</span>
                            <strong>{parseContainerNo(selectedIncident.eventLocation)}</strong>
                          </div>
                        )}
                        {/* Temizlik Zamanı */}
                        {parseCleanTime(selectedIncident?.eventLocation) && (
                          <div className="personnel-card__field">
                            <span>Temizlik Zamanı</span>
                            <strong>{parseCleanTime(selectedIncident.eventLocation)}</strong>
                          </div>
                        )}
                        {/* Durum - parsed from eventLocation */}
                        {parseLocationStatus(selectedIncident?.eventLocation) && (
                          <div className="personnel-card__field">
                            <span>Şikayet anındaki durum</span>
                            <strong style={{ color: '#047857', fontSize: '0.88rem' }}>
                              {parseLocationStatus(selectedIncident.eventLocation)}
                            </strong>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Olay (clean description) ── */}
              <div className="detail-block">
                <span>Olay</span>
                <p>{renderTextWithLinks(cleanDescription) || '-'}</p>
              </div>

              {/* ── Attachments ── */}
              {selectedIncident.attachments?.length > 0 && (
                <div className="attachments">
                  <span>Ekler</span>
                  <div className="attachment-preview-grid">
                    {selectedIncident.attachments.map((attachment) => (
                      <button
                        key={attachment.id}
                        type="button"
                        className="attachment-preview-card"
                        onClick={() => setPreviewAttachment(attachment)}
                      >
                        {isImageAttachment(attachment) ? (
                          <img src={resolveFileUrl(attachment.filePath)} alt={attachment.fileName} />
                        ) : isVideoAttachment(attachment) ? (
                          <video src={resolveFileUrl(attachment.filePath)} muted />
                        ) : (
                          <div className="attachment-file-placeholder">
                            <FaPaperclip />
                          </div>
                        )}
                        <strong>{attachment.mediaType === 'Video' ? 'Video' : 'Fotoğraf'}</strong>
                        <small>{attachment.fileName}</small>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── History ── */}
              <div className="incident-history">
                <span>Düzenleme Geçmişi</span>
                <div className="history-list">
                  {(selectedIncident.histories?.length > 0
                    ? selectedIncident.histories
                    : [{
                      id: 'created',
                      actionText: 'Başvuru yapıldı',
                      createdBy: 'Vatandaş',
                      createdDate: selectedIncident.createdDate,
                      newStatus: selectedIncident.status
                    }]
                  ).map((history) => (
                    <div key={history.id} className="history-item">
                      <div className="history-dot" />
                      <div>
                        <strong>{history.actionText || 'Durum güncellendi'}</strong>
                        <p>
                          {formatDate(history.createdDate)}
                          {history.createdBy ? ` - ${history.createdBy}` : ''}
                        </p>
                        {history.oldStatus && history.newStatus && (
                          <small>
                            {statusLabels[history.oldStatus] || history.oldStatus}
                            {' -> '}
                            {statusLabels[history.newStatus] || history.newStatus}
                          </small>
                        )}
                        {history.note && <em>{history.note}</em>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Status form ── */}
              <div className="status-form">
                <label>Durum</label>
                <select value={statusForm.status} onChange={(e) => setStatusForm((prev) => ({ ...prev, status: e.target.value }))}>
                  {statusOptions.filter((item) => item.value).map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
                <label>Personel Notu</label>
                <textarea
                  rows={4}
                  value={statusForm.staffNote}
                  onChange={(e) => setStatusForm((prev) => ({ ...prev, staffNote: e.target.value }))}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="secondary-button" onClick={() => setSelectedIncident(null)}>Kapat</button>
          <button type="button" className="primary-button" onClick={saveStatus}><FaSave /> Kaydet</button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!previewAttachment} onHide={() => setPreviewAttachment(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{previewAttachment?.fileName || 'Ek Önizleme'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewAttachment && (
            <div className="attachment-lightbox">
              {isImageAttachment(previewAttachment) ? (
                <img src={resolveFileUrl(previewAttachment.filePath)} alt={previewAttachment.fileName} />
              ) : isVideoAttachment(previewAttachment) ? (
                <video src={resolveFileUrl(previewAttachment.filePath)} controls />
              ) : (
                <a href={resolveFileUrl(previewAttachment.filePath)} target="_blank" rel="noreferrer">
                  Dosyayı yeni sekmede aç
                </a>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ReportIncidents;
